/**
 * The one fact every Xero **reference-data** read in this tab has to share:
 * whether Xero has already told us to stop asking, and for how long.
 *
 * ## Why this is a module and not a flag on each hook
 *
 * `XERO_RATE_LIMITED` is not a property of the list being read — it is a
 * property of the **tenant's call budget**, which the whole office shares and
 * every screen in this tab spends from. Five places read
 * `GET /xero/branding-themes` (`InlineInvoiceFields`, `InvoiceFormModal`,
 * `QuoteFormModal`, `CompanyDetailPage`, and `useXeroBrandingThemes` itself)
 * and four more read `GET /xero/tax-rates`. Held per hook, the first form to
 * learn "retry in 49293 seconds" would keep that to itself and the other eight
 * would each go and be refused in turn — one red box per form open, all day.
 *
 * So the cooldown lives here, at module scope, and the two reference-data hooks
 * consult it before issuing anything. **Module scope is the right scope**: it
 * is per tab, which is exactly the lifetime of the knowledge. It is not
 * persisted — a reload is a fresh session, and one probe on a fresh session is
 * a fair price for never being wrong about a cooldown that has since been
 * lifted server-side.
 *
 * ## What this deliberately does NOT cover
 *
 * **Only reference-data reads.** A PUSH (`queueXeroPush`) is a user pressing a
 * button and being told what happened; suppressing that would turn a refusal
 * into silence. This module is for the lists that are fetched *because a form
 * opened*, where nobody asked and nobody can act on the answer.
 */

import { ApiError } from './api';

/**
 * How long to wait when the server reports a rate limit but names no number.
 *
 * ⚠️ Fifteen minutes is a **fallback, never a policy** — `retryAfterSeconds` is
 * authoritative whenever it is present, and nothing here may round, cap or
 * second-guess it.
 *
 * Why this value: Xero enforces several limits at once and the code alone does
 * not say which was hit. The shortest is the per-minute one, which clears in
 * under sixty seconds; the longest is the daily budget, which can be tomorrow.
 * A fallback shorter than a minute would resume hammering a daily-budget
 * refusal, which is the exact failure this module exists to stop, so the error
 * is made in the other direction. Fifteen minutes is long enough that a
 * per-minute breach has certainly cleared, short enough that an unattended tab
 * recovers on its own within a coffee break — and in every case a person can
 * press **Try again**, which clears this outright. That escape hatch is what
 * makes erring long safe.
 */
const FALLBACK_RETRY_AFTER_SECONDS = 15 * 60;

/**
 * When the current cooldown lifts, as an epoch millisecond, or `null` for "no
 * cooldown known".
 *
 * Stored as an ABSOLUTE instant rather than as a countdown, so a form opened
 * ten hours into a fourteen-hour cooldown reports the four hours that are left
 * rather than re-reporting the fourteen it started with.
 */
let cooldownUntil: number | null = null;

/**
 * A cooldown we already knew about, raised so a suppressed read fails down the
 * **same path** as a real refusal.
 *
 * ⚠️ This is the whole trick, and it is a correctness property rather than a
 * tidiness one. Every consumer already has a "the list could not be read"
 * branch that keeps a stored value visible, keeps clearing available, and never
 * renders a blank picker over a real letterhead. A cooldown that returned an
 * empty list instead, or that resolved to `[]`, would take the *success* branch
 * with no data in it — which reads as "this organisation has no themes" and is
 * one stray save from changing the letterhead a customer's PDF prints on. So a
 * suppressed read is a rejection, and the rejection carries the wait.
 */
export class XeroCooldownError extends Error {
  readonly retryAfterSeconds: number;

  constructor(retryAfterSeconds: number) {
    super('Xero’s rate limit has been reached.');
    this.name = 'XeroCooldownError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * The server's own `retryAfterSeconds`, or the documented fallback.
 *
 * ⚠️ Read off `detailPayload`, **not** `details`. `ApiError.details` is narrowed
 * to the per-field validation array and is `undefined` for this error, whose
 * payload is the object `{ retryAfterSeconds, limit }`. Reading the wrong one
 * silently yields the fallback on every 429 and the honest number on none.
 *
 * Anything that is not a finite positive number is treated as absent: a `0`, a
 * negative, or a string would each produce a cooldown that is either useless or
 * permanent, and the fallback is a better answer than both.
 */
function retryAfterFrom(error: ApiError): number {
  const raw = (error.detailPayload as { retryAfterSeconds?: unknown } | null | undefined)
    ?.retryAfterSeconds;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.ceil(raw);
  return FALLBACK_RETRY_AFTER_SECONDS;
}

/**
 * Record a rate limit if that is what this failure is, and report the wait.
 *
 * Answers `null` for every failure that is not a Xero rate limit, so a caller
 * can use it as the first test in its own describer and fall through untouched.
 *
 * ⚠️ **This both reads and WRITES**, deliberately, and it is called from the two
 * `describe…Failure` functions rather than from their call sites. Those two
 * describers are the single funnel every reference-data failure passes through,
 * so recording here means a future consumer gets the suppression by using the
 * existing vocabulary — rather than by remembering to add a line it has no
 * reason to know about. The alternative (record at each `catch`) is one more
 * thing to forget in each of the nine places that read a Xero list.
 *
 * A later refusal REPLACES the stored deadline rather than extending it. That
 * is what requirement 6 needs: **Try again** clears the cooldown, issues a real
 * request, and if that is refused again the new `retryAfterSeconds` is the one
 * that stands. Keeping the older, longer deadline would make a retry unable to
 * ever shorten the wait, and keeping the max of the two would make it unable to
 * ever correct one that was too long.
 */
export function noteXeroRateLimit(caught: unknown): number | null {
  if (caught instanceof XeroCooldownError) return caught.retryAfterSeconds;
  if (!(caught instanceof ApiError) || caught.code !== 'XERO_RATE_LIMITED') return null;
  const seconds = retryAfterFrom(caught);
  cooldownUntil = Date.now() + seconds * 1000;
  return seconds;
}

/**
 * Seconds left on the current cooldown, or `null` when there is none.
 *
 * An expired deadline is forgotten as it is read, so nothing downstream has to
 * remember to sweep it. Never answers `0`: a cooldown with no time left is not
 * a cooldown, and `0` is a value a caller would have to test separately from
 * `null` to avoid rendering "wait about 0 minutes".
 */
export function xeroCooldownSecondsRemaining(): number | null {
  if (cooldownUntil === null) return null;
  const remaining = cooldownUntil - Date.now();
  if (remaining <= 0) {
    cooldownUntil = null;
    return null;
  }
  return Math.ceil(remaining / 1000);
}

/**
 * Forget the cooldown, so the very next read actually goes out.
 *
 * Called by every `reload()` in the reference-data hooks. Without it a **Try
 * again** button would be swallowed by the suppression it is offered to escape
 * — the worst of both, since the reader is told there is something they can do
 * and then nothing happens.
 */
export function clearXeroCooldown(): void {
  cooldownUntil = null;
}

/**
 * A wait, said the way a person would say it.
 *
 * Rounded on purpose: `49293 seconds` is precise, unreadable, and wrong within
 * a minute of being printed. "about 14 hours" is what the reader needs — that
 * this is not a blip and not something to sit and wait out.
 *
 * Hours are used up to two days rather than switching at 24, because "about 30
 * hours" is a clearer statement about a shared daily budget than "about 1 day",
 * which reads as a policy rather than a countdown.
 */
export function describeXeroWait(seconds: number): string {
  if (seconds <= 60) return 'less than a minute';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.round(seconds / 3600);
  if (hours < 48) return `about ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = Math.round(seconds / 86400);
  return `about ${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * The sentence a reader who has never heard of a rate limit needs.
 *
 * Three claims, and all three are load-bearing:
 *
 * 1. **What happened** — Xero's limit, not our failure. "The list could not be
 *    loaded" is the wording this replaces, and it is the reason someone spent a
 *    morning re-checking a connection that was healthy the whole time.
 * 2. **Roughly how long** — so the reader can decide to come back rather than
 *    sit on the form pressing things.
 * 3. **That nothing they do will help** — the budget is the tenant's and it is
 *    shared across the office, so reloading, reconnecting and re-opening the
 *    form all cost a request and buy nothing.
 *
 * `subject` names the list in the reader's own vocabulary — "branding themes",
 * "tax rates" — because a sentence about the wrong control sends them to the
 * wrong control.
 */
export function xeroCooldownMessage(subject: string, seconds: number): string {
  return (
    `Xero’s rate limit has been reached, so its ${subject} can’t be read for ${describeXeroWait(seconds)}. ` +
    'Nothing is broken and nothing here will free it up sooner — the Xero call budget is shared across the ' +
    'whole office and it is spent. SEED EMERGE has stopped asking until it resets.'
  );
}

/**
 * The same fact in one clause, for a place with no room for the full sentence —
 * a folded panel header, a summary line.
 *
 * Worded so it cannot be read as a different claim from
 * {@link xeroCooldownMessage} about the same tenant, which is the trap a
 * shortened duplicate usually falls into.
 */
export function xeroCooldownSummary(subject: string, seconds: number): string {
  return `Xero’s rate limit has been reached — its ${subject} are unavailable for ${describeXeroWait(seconds)}.`;
}

/**
 * The sentence for a record that was SAVED HERE but not sent to Xero, because
 * the tenant is in a cooldown and the call would have been refused.
 *
 * This is a third sentence rather than a reuse of {@link xeroCooldownMessage},
 * and the difference is not a matter of tone. That one is about a list we could
 * not READ, and it ends by telling the reader that nothing they do will help —
 * which is true there, because the control they are looking at has no other way
 * to get its data.
 *
 * Here the opposite is true in both halves:
 *
 * - **Something did succeed.** The {@link subject} exists, with everything on
 *   it. Leading with the limit would read as "your invoice failed", and the
 *   likely response is to create it again — which is how a cooldown turns into
 *   duplicate records in an accounting system.
 * - **There IS a recovery action, and it is theirs.** Nothing retries this. If
 *   the sentence does not say the record can be sent from its own page later,
 *   the honest outcome is a document that silently never reaches Xero, and the
 *   first person to notice is whoever reconciles the month.
 *
 * So it states, in order: it saved, it is not in Xero yet, why that is not a
 * failure, roughly how long, and who sends it.
 *
 * `subject` names the record in the reader's vocabulary — "quote", "invoice" —
 * singular, because this always concerns the one they just created.
 */
export function xeroCooldownNotSentMessage(subject: string, seconds: number): string {
  return (
    `This ${subject} has been saved. It has not been sent to Xero yet: Xero’s rate limit has been reached, ` +
    `and the call budget is shared across the whole office. It should be available again in ` +
    `${describeXeroWait(seconds)} — nothing is broken, and this ${subject} can be sent to Xero from its own ` +
    `page once it is.`
  );
}
